import React from 'react'
import { Button, Flex, H2, Input, Line, SelectDropdown } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { GetWebinarsSearchParams } from '../../lib/types'
import { blogSort } from '../../lib/utils/message'
import { WebinarCard } from './webinar-card'
import './webinar-list.scss'
import { WEBINAR_STATUSES } from '../webinars-form/constants'

type WebinarListPresenterProps = {
  webinars: Array<{
    id: number
    thumbnail_url: string
    title: string
    description: string
    youtube_video_id: string
    status: 'DRAFT' | 'PUBLISHED'
    published_at: string
    created_at: string
    updated_at: string
  }>
  searchParams: GetWebinarsSearchParams
  handleSearchParams: (params: { name: string; value: string }) => void
  resetSearchParams: () => void
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleCurrentPage: (currentPage: number) => void
  currentPage: number
  handleWebinarEdit: (id: number) => void
}

function WebinarListPresenter({
  webinars,
  searchParams,
  handleSearchParams,
  resetSearchParams,
  pagination,
  handleCurrentPage,
  currentPage,
  handleWebinarEdit,
}: WebinarListPresenterProps) {
  const { breakpoint } = useBreakpoint()

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Browse Webinars' />

      <div className='blog-list-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={blogSort}
            placeholder={'Sort'}
            value={searchParams.sortBy}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='webinar-list-sort-select'
          />
          <SelectDropdown
            name='status'
            id='statusId'
            formId=''
            list={WEBINAR_STATUSES as { text: string; value: string }[]}
            placeholder={'Status'}
            value={searchParams.status || ''}
            handleChange={(val: string) => handleSearchParams({ name: 'status', value: val })}
            dataTestId='webinar-list-status-select'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>

      <Line />
      {webinars && webinars.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='webinars-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Status</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>YouTube ID</th>
                    <th>Published At</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {webinars.map(
                    ({
                      id,
                      thumbnail_url,
                      title,
                      description,
                      youtube_video_id,
                      status,
                      published_at,
                      created_at,
                      updated_at,
                    }) => (
                      <WebinarCard
                        key={id}
                        id={id}
                        thumbnailUrl={thumbnail_url}
                        title={title}
                        description={description}
                        youtubeVideoId={youtube_video_id}
                        status={status}
                        publishedAt={published_at}
                        createdAt={created_at}
                        updatedAt={updated_at}
                        handleEditClick={handleWebinarEdit}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <H2 classes={['mt-48']} text='No webinars found' />
      )}
    </Flex>
  )
}

export { WebinarListPresenter }
